"use client";
import { useEffect, useState, memo } from "react";
import {
  User,
  Shield,
  Search,
  FileText,
  CheckCircle,
  Camera,
} from "lucide-react";
import "./orbiting-skills.css";

// --- Improved SVG Icon Components ---
const iconComponents = {
  identity: {
    component: () => (
      <span className="r2-orbiting-skills__svg-wrap">
        <User className="r2-orbiting-skills__svg" strokeWidth={2} />
      </span>
    ),
    color: "#A855F7", // purple
  },
  criminal: {
    component: () => (
      <span className="r2-orbiting-skills__svg-wrap">
        <Search className="r2-orbiting-skills__svg" strokeWidth={2} />
      </span>
    ),
    color: "#3B82F6", // blue
  },
  mvr: {
    component: () => (
      <span className="r2-orbiting-skills__svg-wrap">
        <FileText className="r2-orbiting-skills__svg" strokeWidth={2} />
      </span>
    ),
    color: "#10B981", // green
  },
  insurance: {
    component: () => (
      <span className="r2-orbiting-skills__svg-wrap">
        <Shield className="r2-orbiting-skills__svg" strokeWidth={2} />
      </span>
    ),
    color: "#06B6D4", // cyan
  },
  license: {
    component: () => (
      <span className="r2-orbiting-skills__svg-wrap">
        <CheckCircle className="r2-orbiting-skills__svg" strokeWidth={2} />
      </span>
    ),
    color: "#F59E0B", // yellow/orange
  },
  selfie: {
    component: () => (
      <span className="r2-orbiting-skills__svg-wrap">
        <Camera className="r2-orbiting-skills__svg" strokeWidth={2} />
      </span>
    ),
    color: "#F97316", // orange/peach
  },
};

// --- Memoized Icon Component ---
const SkillIcon = memo(({ type }) => {
  const IconComponent = iconComponents[type]?.component;
  return IconComponent ? <IconComponent /> : null;
});
SkillIcon.displayName = "SkillIcon";

// --- Configuration for the Orbiting Skills ---
const skillsConfig = [
  // Inner Orbit - 3 items
  {
    id: "identity",
    orbitRadius: 120,
    size: 50,
    speed: 1,
    iconType: "identity",
    phaseShift: 0,
    glowColor: "cyan",
    label: "Identity Verification",
    themeClass: "r2-orbiting-skills__badge--identity",
  },
  {
    id: "criminal",
    orbitRadius: 120,
    size: 50,
    speed: 1,
    iconType: "criminal",
    phaseShift: (2 * Math.PI) / 3,
    glowColor: "cyan",
    label: "Criminal Record",
    themeClass: "r2-orbiting-skills__badge--criminal",
  },
  {
    id: "mvr",
    orbitRadius: 120,
    size: 50,
    speed: 1,
    iconType: "mvr",
    phaseShift: (4 * Math.PI) / 3,
    glowColor: "cyan",
    label: "Access MVR Data",
    themeClass: "r2-orbiting-skills__badge--mvr",
  },
  // Outer Orbit - 3 items
  {
    id: "insurance",
    orbitRadius: 200,
    size: 50,
    speed: -0.6,
    iconType: "insurance",
    phaseShift: 0,
    glowColor: "purple",
    label: "Insurance Validation",
    themeClass: "r2-orbiting-skills__badge--insurance",
  },
  {
    id: "license",
    orbitRadius: 200,
    size: 50,
    speed: -0.6,
    iconType: "license",
    phaseShift: (2 * Math.PI) / 3,
    glowColor: "purple",
    label: "License Verification",
    themeClass: "r2-orbiting-skills__badge--license",
  },
  {
    id: "selfie",
    orbitRadius: 200,
    size: 50,
    speed: -0.6,
    iconType: "selfie",
    phaseShift: (4 * Math.PI) / 3,
    glowColor: "purple",
    label: "Selfie Biometrics",
    themeClass: "r2-orbiting-skills__badge--selfie",
  },
];

// --- Memoized Orbiting Skill Component ---
const OrbitingSkill = memo(({ config, angle }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { orbitRadius, iconType, label, themeClass } = config;

  const x = Math.cos(angle) * orbitRadius;
  const y = Math.sin(angle) * orbitRadius;

  return (
    <div
      className="r2-orbiting-skills__orbiting-skill"
      style={{
        transform: `translate(calc(${x}px - 50%), calc(${y}px - 50%))`,
        zIndex: isHovered ? 20 : 10,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`r2-orbiting-skills__badge ${themeClass} ${
          isHovered
            ? "r2-orbiting-skills__badge--expanded"
            : "r2-orbiting-skills__badge--collapsed"
        }`}
        style={{
          width: isHovered ? "auto" : "50px",
          height: "50px",
          justifyContent: isHovered ? "flex-start" : "center",
          paddingLeft: isHovered ? "16px" : "0",
          paddingRight: isHovered ? "16px" : "0",
        }}
      >
        <div className="r2-orbiting-skills__icon">
          <SkillIcon type={iconType} />
        </div>
        <span
          className={`r2-orbiting-skills__label ${
            isHovered
              ? "r2-orbiting-skills__label--visible"
              : "r2-orbiting-skills__label--hidden"
          }`}
        >
          {label}
        </span>
      </div>
    </div>
  );
});
OrbitingSkill.displayName = "OrbitingSkill";

// Comment out this version and uncomment the blue version above to switch to blue colors
const GlowingOrbitPath = memo(
  ({ radius, glowColor = "cyan", animationDelay = 0 }) => {
    const glowColors = {
      cyan: {
        primary: "rgba(34, 211, 238, 0.3)", // cyan
        secondary: "rgba(103, 232, 249, 0.2)",
        border: "rgba(6, 182, 212, 0.4)",
        shadow: "rgba(34, 211, 238, 0.5)",
      },
      purple: {
        primary: "rgba(168, 85, 247, 0.4)", // purple
        secondary: "rgba(192, 132, 252, 0.25)",
        border: "rgba(147, 51, 234, 0.5)",
        shadow: "rgba(168, 85, 247, 0.6)",
      },
    };

    const colors = glowColors[glowColor] || glowColors.cyan;

    return (
      <div
        className="r2-orbiting-skills__orbit"
        style={{
          width: `${radius * 2}px`,
          height: `${radius * 2}px`,
        }}
      >
        <div
          className="r2-orbiting-skills__orbit-glow"
          style={{
            background: `radial-gradient(circle, transparent 25%, ${colors.secondary} 60%, ${colors.primary} 100%)`,
            boxShadow: `0 0 60px ${colors.shadow}, 0 0 40px ${colors.primary}, inset 0 0 50px ${colors.secondary}`,
            animationDelay: `${animationDelay}s`,
          }}
        />

        <div
          className="r2-orbiting-skills__orbit-border"
          style={{
            border: `2px solid ${colors.border}`,
            boxShadow: `inset 0 0 30px ${colors.secondary}, 0 0 30px ${colors.primary}`,
          }}
        />
      </div>
    );
  }
);
GlowingOrbitPath.displayName = "GlowingOrbitPath";

// --- Main App Component ---
export default function OrbitingSkills() {
  const [time, setTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    let animationFrameId;
    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setTime((prevTime) => prevTime + deltaTime);
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPaused]);

  const orbitConfigs = [
    { radius: 120, glowColor: "cyan", delay: 0 },
    { radius: 200, glowColor: "purple", delay: 1.5 },
  ];

  return (
    <main className="r2-orbiting-skills">
      <div
        className="r2-orbiting-skills__stage"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="r2-orbiting-skills__core">
          <div className="r2-orbiting-skills__core-glow r2-orbiting-skills__core-glow--cyan r2-orbiting-skills__blur-xl"></div>
          <div
            className="r2-orbiting-skills__core-glow r2-orbiting-skills__core-glow--purple-500 r2-orbiting-skills__blur-2xl"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="r2-orbiting-skills__core-glow r2-orbiting-skills__core-glow--purple-600 r2-orbiting-skills__blur-3xl"
            style={{ animationDelay: "2s" }}
          ></div>
          <div className="r2-orbiting-skills__core-content">
            <User className="r2-orbiting-skills__core-icon" strokeWidth={2} />
            <span className="r2-orbiting-skills__core-label">Renter</span>
          </div>
        </div>

        {/* Render glowing orbit paths */}
        {orbitConfigs.map((config) => (
          <GlowingOrbitPath
            key={`path-${config.radius}`}
            radius={config.radius}
            glowColor={config.glowColor}
            animationDelay={config.delay}
          />
        ))}

        {/* Render orbiting skill icons */}
        {skillsConfig.map((config) => {
          const angle = time * config.speed + (config.phaseShift || 0);
          return (
            <OrbitingSkill key={config.id} config={config} angle={angle} />
          );
        })}
      </div>
    </main>
  );
}
